import { Immutable } from "immer";

import {
    IAddon,
    IConcreteBundle,
    IOptionValues,
    IProduct,
    IVerboseProductClass,
} from "../../models/catalogue";
import { FinancingPlans } from "../../models/financing";
import { IProductCategoryID, IProductID } from "../../models/nominals";
import { IAPIPrice } from "../../models/prices";
import { IProductShippingMethod } from "../../models/shipping";
import {
    ICardDrawerSettings,
    IModularConfiguratorPromoCard,
    IModularConfiguratorUpsellInfoModal,
    IPLCOptionPanel,
    IPLCProductCategorySelector,
    IVariantPrefilter,
} from "./models";

export enum RootSelectionMode {
    DIRECT = "direct",
    CATEGORY = "category",
}

export type ISelectedCategories = Readonly<{
    [selectorID: string]: IProductCategoryID;
}>;

type IRootSelectionModeDirect = Readonly<{
    type: RootSelectionMode.DIRECT;
    productID: IProductID | null;
}>;

type IRootSelectionModeCategory = Readonly<{
    type: RootSelectionMode.CATEGORY;
    categories: ISelectedCategories;
}>;

export type IRootSelectionMode =
    | IRootSelectionModeDirect
    | IRootSelectionModeCategory;

type ISelectionState = Immutable<{
    // Root Product
    selectedRoot: IRootSelectionMode | null;

    // Product option selector state
    optionValues: IOptionValues;
    quantity: number;
}>;

type IUIState = Immutable<{
    // Root Product
    selectedRoot: IRootSelectionMode | null;

    // Product option selector state
    optionValues: IOptionValues;
    quantity: number;

    // Track the previous selection state to allow selection "undo"
    previousSelectionState: ISelectionState | null;

    // Product Upgrades
    selectedUpgrade: IProductID | null;

    // Product Add-Ons
    selectedAddons: readonly IAddon[];

    // Product Upgrades / Add-Ons Shared
    // The product ID to show info about when the modal is open, null is the modal is closed.
    upsellInfoModalActiveBundle: number | null;

    // Add to basket button
    addToBasketCooldownActive: boolean;
    addToBasketButtonText: string;

    // Add to basket error modal
    addToBasketErrorOpen: boolean;
    addToBasketErrorReason: string;

    // Ratings Modal
    ratingsModalOpen: boolean;

    // Option Selection Error
    optionSelectionErrorReason: string;

    // Options panel
    panelTriggerClicked: boolean;

    // Product card drawers: sold-out root products kept in the selector
    // because the customer arrived on them (availability filtering rule 6)
    keptRootProductIDs: readonly IProductID[];
}>;

export type IReduxState = Immutable<{
    // Product Data
    entities: {
        rootProducts: readonly IProduct[];
        variantPrefilter: IVariantPrefilter | null;
        cardDrawers: ICardDrawerSettings | null;
        categorySelectors?: IPLCProductCategorySelector[];
        optionPanels?: IPLCOptionPanel[];
        price: IAPIPrice | null;
        priceError: string | null;
        lastPriceRequestId: string | null;
        financingPlans?: FinancingPlans;
        concreteBundles: readonly IConcreteBundle[];
        // Array of all upsell info modals available to show
        upsellInfoModals: IModularConfiguratorUpsellInfoModal[];
        promoCards: IModularConfiguratorPromoCard[];
        productClass: IVerboseProductClass | null;
    };

    // UI State Data
    ui: IUIState;

    shippingMethod: IProductShippingMethod | null;
}>;
